import { useFormikContext } from "formik";
import { useTranslations } from "use-intl";
import { IncidentType } from "@/gql/graphql";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { DateTimeInput } from "@/lib/Components/Form/Inputs/DateTimeInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { formatDateTimeLocal } from "@/lib/Formatters/formatDateTime";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { incidentWorkOrderOptionQuery } from "@/app/Incidents/GraphQL/incidentWorkOrderOptionQuery";
import { IncidentWorkOrderInput } from "@/app/Incidents/Components/IncidentWorkOrderInput";
import { IncidentClaimFields } from "@/app/Incidents/Components/IncidentClaimFields";
import { ReportIncidentFormValues } from "@/app/Incidents/Forms/reportIncidentSchema";
import { SUPPLIER_MIN_PHOTOS } from "@/app/Incidents/Utils/supplierReporting";
import {
  INCIDENT_UPLOAD_MAX_FILES,
  INCIDENT_UPLOAD_MAX_SIZE,
} from "@/app/Incidents/Utils/incidentUploads";
import { GenericField } from "@/lib/Components/Form/Fields/GenericField";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { Button } from "@/components/catalyst/button";

export function ReportIncidentForm({
  canPickAssignment,
}: {
  canPickAssignment: boolean;
}) {
  const t = useTranslations("incident");
  const { isAdmin } = useTenant();
  const { values, setFieldValue } =
    useFormikContext<ReportIncidentFormValues>();

  const { data } = useGqlQuery(
    incidentWorkOrderOptionQuery,
    { id: values.work_order_id },
    { enabled: !!values.work_order_id },
  );
  const workOrder = data?.workOrder ?? null;

  const typeOptions = Object.values(IncidentType).map((type) => ({
    label: t(`type.${type}`),
    value: type,
  }));

  const assignmentOptions = (workOrder?.assignments ?? []).map(
    (assignment) => ({
      label: assignment.driver_delivered_at
        ? t("report.driverOptionDelivered", {
            name: assignment.proDriver.name ?? "--",
            date: formatDateTimeLocal(assignment.driver_delivered_at) ?? "",
          })
        : (assignment.proDriver.name ?? "--"),
      value: assignment.id,
    }),
  );

  return (
    <>
      {canPickAssignment ? (
        <>
          <IncidentWorkOrderInput
            name="work_order_id"
            label={t("report.workOrder")}
            className="col-span-6"
            onChange={(selected) => {
              const assignments = selected?.assignments ?? [];
              setFieldValue(
                "work_order_assignment_id",
                assignments.length === 1 ? assignments[0].id : "",
              );
            }}
          />
          <SelectInput
            name="work_order_assignment_id"
            label={t("report.assignment")}
            className="col-span-6"
            disabled={!workOrder}
            placeholder={
              workOrder
                ? assignmentOptions.length === 0
                  ? t("report.noDrivers")
                  : undefined
                : t("report.pickWorkOrderFirst")
            }
            options={[{ label: "", options: assignmentOptions }]}
          />
        </>
      ) : null}

      <DateTimeInput
        name="found_at"
        label={t("report.foundAt")}
        className="col-span-6"
      />
      <SelectInput
        name="type"
        label={t("report.type")}
        className="col-span-6"
        options={[{ label: "", options: typeOptions }]}
      />
      <TextAreaInput
        name="description"
        label={t("report.description")}
        className="col-span-full"
        textAreaProps={{
          rows: 4,
          maxLength: 5000,
          placeholder: t("report.descriptionPlaceholder"),
        }}
      />
      <TextInput
        name="follow_up_contact"
        label={t("report.followUpContact")}
        description={t("report.followUpContactDescription")}
        className="col-span-6"
      />

      <GenericField
        name="photos"
        label={t("report.photos")}
        description={
          isAdmin
            ? undefined
            : t("report.photosHint", { count: SUPPLIER_MIN_PHOTOS })
        }
        className="col-span-full"
        viewNode={null}
      >
        <div className="space-y-2">
          {values.photos.map((photo) => (
            <div
              key={photo.filePath}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
            >
              <span className="truncate">{photo.file.name}</span>
              <Button
                plain
                type="button"
                onClick={() =>
                  setFieldValue(
                    "photos",
                    values.photos.filter(
                      (upload) => upload.filePath !== photo.filePath,
                    ),
                  )
                }
              >
                {t("report.remove")}
              </Button>
            </div>
          ))}
          <FileUploader
            compact
            maxFileCount={INCIDENT_UPLOAD_MAX_FILES}
            maxSize={INCIDENT_UPLOAD_MAX_SIZE}
            onUpload={async (uploads) => {
              await setFieldValue("photos", [...values.photos, ...uploads]);
            }}
          />
        </div>
      </GenericField>

      {workOrder ? (
        <>
          <FormDivider />
          <SwitchInput
            name="open_claim"
            label={t("report.openClaim")}
            description={t("report.openClaimHint")}
            className="col-span-full"
          />
          {values.open_claim ? (
            <IncidentClaimFields currency={workOrder.currency} />
          ) : null}
        </>
      ) : null}
    </>
  );
}
